import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';

/**
 * Sprint 98 — El contenedor de captura, una sola vez.
 *
 * ## Por qué existe
 *
 * En este sistema, capturar datos siempre fue una **ventana sobre lo que estabas haciendo**:
 * `FormProducto`, `FormPaquete`, `FormInsumo`, `FormMesa`, `FormCategoria`, `FormPromocion`,
 * `CobroModal`, `StepUpModal`… doce pantallas con el mismo `fixed inset-0` copiado a mano. Las
 * únicas excepciones eran los pasos del pedido a domicilio, que **mudaban de pantalla** para pedir
 * dos campos. Un operador aprende un patrón, no dos.
 *
 * Al agregar tres capturas más al flujo de domicilio, la opción era copiar el `div` por
 * decimotercera vez o darle nombre. Esto es el nombre. Los doce existentes **no se migran en este
 * sprint** —tocar el cobro y el step-up para un cambio cosmético es riesgo sin beneficio—, pero lo
 * nuevo ya nace de aquí.
 *
 * ## Las dos decisiones que no son cosméticas
 *
 * 1. **No cierra por clic afuera ni con `Escape`.** Estos modales se abren a media captura de un
 *    pedido, con el cliente al teléfono. Un clic distraído fuera de la ventana no puede tirar lo
 *    que se lleva escrito: la salida es siempre un botón que dice a dónde va.
 *    Hay un motivo concreto además del principio: `MapaPicker` se monta **encima** del paso
 *    "¿a dónde?" y no escucha `Escape`. Si esta ventana sí lo escuchara, apretarlo con el mapa
 *    abierto cerraría la de abajo y dejaría el mapa huérfano sobre una pantalla que ya no existe.
 * 2. **El pie no scrollea.** En la laptop del negocio (1280×661) el paso de la dirección no cabe
 *    entero; si los botones vivieran al final del contenido, "Continuar" quedaría debajo del borde
 *    y el operador creería que la ventana está trabada. Scrollea el cuerpo, nunca la salida.
 */
type Ancho = 'md' | 'lg' | 'xl' | '3xl';

const ANCHOS: Record<Ancho, string> = {
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '3xl': 'max-w-3xl',
};

interface Props {
  abierto: boolean;
  titulo: string;
  /** Línea bajo el título: el paso, el nombre del cliente, el para-qué. */
  subtitulo?: ReactNode;
  children: ReactNode;
  /** Barra inferior fija. Va DENTRO del `<form>` cuando se pasa `onSubmit`. */
  pie?: ReactNode;
  ancho?: Ancho;
  /**
   * Si se pasa, el contenedor es un `<form>` y `Enter` en cualquier campo confirma.
   * Capturar un nombre y apretar Enter es el gesto de quien contesta el teléfono.
   */
  onSubmit?: (e: React.FormEvent) => void;
  /** Si se pasa, se pinta la X. Sin esto, la única salida son los botones del pie. */
  onCerrar?: () => void;
  etiquetaCerrar?: string;
  /** Para que los tests y la ayuda contextual puedan apuntarle. */
  dataTour?: string;
}

export function Modal({
  abierto,
  titulo,
  subtitulo,
  children,
  pie,
  ancho = 'md',
  onSubmit,
  onCerrar,
  etiquetaCerrar = 'Cerrar',
  dataTour,
}: Props) {
  // Con la ventana abierta, lo de atrás no scrollea: el fondo es contexto, no terreno.
  useEffect(() => {
    if (!abierto) return;
    const previo = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previo;
    };
  }, [abierto]);

  if (!abierto) return null;

  const idTitulo = `modal-${titulo.replace(/\s+/g, '-').toLowerCase()}`;
  const Contenedor = onSubmit ? 'form' : 'div';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={idTitulo}
      data-tour={dataTour}
    >
      <Contenedor
        onSubmit={onSubmit}
        className={`flex max-h-[90vh] w-full ${ANCHOS[ancho]} flex-col overflow-hidden rounded-xl bg-white shadow-2xl`}
      >
        <div className="relative shrink-0 border-b border-slate-200 px-6 py-4 text-center">
          <h2 id={idTitulo} className="text-2xl font-bold">
            {titulo}
          </h2>
          {subtitulo && <p className="mt-1 text-sm text-slate-500">{subtitulo}</p>}
          {onCerrar && (
            <button
              type="button"
              onClick={onCerrar}
              aria-label={etiquetaCerrar}
              className="absolute right-3 top-3 rounded-lg p-2 text-slate-500 hover:bg-slate-100"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">{children}</div>

        {pie && (
          <div className="shrink-0 border-t border-slate-200 bg-slate-50 px-6 py-4">{pie}</div>
        )}
      </Contenedor>
    </div>
  );
}
